// frontend/src/pages/StorefrontPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { storesApi } from '../api/stores';
import { vehiclesApi } from '../api/vehicles';
import { partsApi } from '../api/parts'; // We'll need to filter parts by store_id manually or add a filter
import { 
    FaStore, FaMapMarkerAlt, FaPhone, FaEnvelope, FaCheckCircle, 
    FaCar, FaBoxOpen, FaShareAlt, FaSpinner, FaWhatsapp 
} from 'react-icons/fa';
import VehicleCard from '../components/vehicle/VehicleCard';
import PartCard from '../components/part/PartCard';
import DynamicMeta from '../components/common/DynamicMeta';

function StorefrontPage() {
  const { storeId } = useParams();
  const [store, setStore] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'vehicles', 'parts'
  const [copyMessage, setCopyMessage] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 1. Fetch Store Details
        const storeData = await storesApi.getPublicDetails(storeId);
        setStore(storeData);

        if (storeData) {
            // 2. Fetch Inventory (We fetch all and filter client-side for now, or use specific API filters if available)
            // Note: Ideally, backend should support ?store_id=XYZ on these endpoints.
            // vehiclesApi.getAllVehicles already supports filters, but we need to ensure it supports store_id if we added it.
            // Let's assume we fetch all and filter for this MVP step, or update the API later.
            
            // Actually, let's use the existing "getSellerVehicles" logic but adapted.
            // Since we don't have a direct "getVehiclesByStore" public endpoint, we might need to rely on 
            // the fact that getAllVehicles returns everything and we filter.
            // Optimization: We should add store_id filter to getAllVehicles/getAllParts in backend.
            // For now, let's try fetching all and filtering.
            
            const [allVehicles, allParts] = await Promise.all([
                vehiclesApi.getAllVehicles(),
                partsApi.getAllParts()
            ]);

            setVehicles(allVehicles.filter(v => v.store_id === storeId));
            setParts(allParts.filter(p => p.store_id === storeId));
        }
      } catch (err) {
        console.error("Failed to load storefront", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [storeId]);

  const handleShare = () => {
      navigator.clipboard.writeText(window.location.href);
      setCopyMessage('Link Copied!');
      setTimeout(() => setCopyMessage(''), 2000);
  };

  if (loading) return <div className="flex justify-center items-center min-h-screen"><FaSpinner className="animate-spin text-4xl text-primary" /></div>;
  if (!store) return <div className="text-center py-20">Store not found.</div>;

  const displayedVehicles = activeTab === 'all' || activeTab === 'vehicles' ? vehicles : [];
  const displayedParts = activeTab === 'all' || activeTab === 'parts' ? parts : [];

  return (
    <div className="bg-gray-50 min-h-screen pb-20">
      <DynamicMeta 
          title={`${store.store_name} - Everything Automotive`}
          description={`Browse inventory from ${store.store_name}. ${store.address}, ${store.lga_name}.`}
      />

      {/* --- HEADER BANNER --- */}
      <div className="bg-white border-b border-gray-200">
          <div className="container mx-auto px-4 py-8 md:py-12">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                  
                  {/* Logo / Icon */}
                  <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center border-4 border-white shadow-lg flex-shrink-0">
                      {store.kyc_photo_url ? (
                          <img src={store.kyc_photo_url} alt={store.store_name} className="w-full h-full object-cover rounded-full" />
                      ) : (
                          <FaStore className="text-4xl text-gray-400" />
                      )}
                  </div>

                  {/* Info */}
                  <div className="flex-grow">
                      <div className="flex items-center gap-2 mb-1">
                          <h1 className="text-3xl font-bold text-gray-900">{store.store_name}</h1>
                          <FaCheckCircle className="text-blue-500 text-xl" title="Verified Partner" />
                      </div>
                      <p className="text-gray-600 flex items-center gap-2 mb-2">
                          <FaMapMarkerAlt className="text-red-500" /> 
                          {store.address}, {store.lga_name}, {store.state}
                      </p>
                      <div className="flex flex-wrap gap-3">
                          {store.phone && (
                              <a href={`tel:${store.phone}`} className="text-sm bg-green-50 text-green-700 px-3 py-1 rounded-full font-bold flex items-center gap-1 hover:bg-green-100">
                                  <FaPhone size={12} /> {store.phone}
                              </a>
                          )}
                          {store.email && (
                              <a href={`mailto:${store.email}`} className="text-sm bg-blue-50 text-blue-700 px-3 py-1 rounded-full font-bold flex items-center gap-1 hover:bg-blue-100">
                                  <FaEnvelope size={12} /> Email
                              </a>
                          )}
                          <button onClick={handleShare} className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-bold flex items-center gap-1 hover:bg-gray-200">
                              <FaShareAlt size={12} /> {copyMessage || 'Share Store'}
                          </button>
                      </div>
                  </div>

                  {/* WhatsApp CTA */}
                  {store.phone && (
                      <a 
                          href={`https://wa.me/${store.phone.replace('+', '')}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-bold flex items-center gap-2 shadow-md transition-transform hover:scale-105"
                      >
                          <FaWhatsapp size={20} /> Chat on WhatsApp
                      </a>
                  )}
              </div>
          </div>
      </div>

      {/* --- TABS --- */}
      <div className="container mx-auto px-4 py-6">
          <div className="flex gap-4 border-b border-gray-200 mb-6">
              <button 
                  onClick={() => setActiveTab('all')}
                  className={`pb-2 px-4 font-bold text-sm transition-colors ${activeTab === 'all' ? 'border-b-2 border-primary text-primary' : 'text-gray-500 hover:text-gray-700'}`}
              >
                  All Items ({vehicles.length + parts.length})
              </button>
              <button 
                  onClick={() => setActiveTab('vehicles')}
                  className={`pb-2 px-4 font-bold text-sm transition-colors ${activeTab === 'vehicles' ? 'border-b-2 border-primary text-primary' : 'text-gray-500 hover:text-gray-700'}`}
              >
                  Vehicles ({vehicles.length})
              </button>
              <button 
                  onClick={() => setActiveTab('parts')}
                  className={`pb-2 px-4 font-bold text-sm transition-colors ${activeTab === 'parts' ? 'border-b-2 border-primary text-primary' : 'text-gray-500 hover:text-gray-700'}`}
              >
                  Parts ({parts.length})
              </button>
          </div>

          {/* --- INVENTORY GRID --- */}
          <div className="space-y-8">
              
              {/* Vehicles Section */}
              {displayedVehicles.length > 0 && (
                  <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                          <FaCar className="text-primary" /> Vehicles
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                          {displayedVehicles.map(v => (
                              <VehicleCard key={v.id} vehicle={v} />
                          ))}
                      </div>
                  </div>
              )}

              {/* Parts Section */}
              {displayedParts.length > 0 && (
                  <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                          <FaBoxOpen className="text-primary" /> Parts & Accessories
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                          {displayedParts.map(p => (
                              <PartCard key={p.id} part={p} />
                          ))}
                      </div>
                  </div>
              )}

              {displayedVehicles.length === 0 && displayedParts.length === 0 && (
                  <div className="text-center py-20 bg-white rounded-lg border border-dashed border-gray-300">
                      <p className="text-gray-500 text-lg">This store has no active inventory.</p>
                  </div>
              )}
          </div>
      </div>
    </div>
  );
}

export default StorefrontPage;